import { useState } from "react";
import { toast } from "sonner";
import { pu, eur } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Plus, Trash2, Save } from "lucide-react";

const num = (v) => Number(String(v).replace(",", ".")) || 0;

/** Basi e prezzo ingrediente della «Pizza inventata»: la sala compone e il prezzo si somma. */
export const PizzaInventata = () => {
  const { settings, refresh } = useApp();
  const [basi, setBasi] = useState(() => settings?.pizza_bases || []);
  const [formati, setFormati] = useState(() => settings?.pizza_formats || []);
  const [impasti, setImpasti] = useState(() => settings?.pizza_doughs || []);
  const [ingPrezzo, setIngPrezzo] = useState(String(settings?.ingredient_price ?? 1.5).replace(".", ","));
  const [nome, setNome] = useState("");
  const [prezzo, setPrezzo] = useState("5,00");
  const [fNome, setFNome] = useState("");
  const [fPrezzo, setFPrezzo] = useState("0,00");
  const [iNome, setINome] = useState("");
  const [iPrezzo, setIPrezzo] = useState("0,00");
  const [busy, setBusy] = useState(false);

  const salva = async (next = basi, forms = formati) => {
    setBusy(true);
    try {
      await pu("/settings", { ...settings, pizza_bases: next, pizza_formats: forms,
                              pizza_doughs: impasti, ingredient_price: num(ingPrezzo) });
      setBasi(next);
      setFormati(forms);
      toast.success("Salvato: la sala compone la pizza con questi prezzi");
      refresh?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Non salvato");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className="space-y-3 p-5" data-testid="pizza-inventata-card">
      <div>
        <h2 className="font-head text-base font-bold">Pizza speciale: basi e prezzi</h2>
        <p className="mt-1 text-xs text-slate-500">
          È la pizza inventata dal cliente. In comanda il cameriere scegle la base e il formato
          (normale, baby, sottile), tocca gli ingredienti che dice il cliente e il prezzo si somma
          da solo. Ogni ingrediente ha il suo prezzo (bresaola e wurstel non costano uguale): quelli
          senza prezzo valgono la cifra qui sotto. Chi è in cassa può sempre correggere il prezzo.
        </p>
      </div>

      <div className="flex flex-wrap gap-2" data-testid="basi-lista">
        {basi.map((b, idx) => (
          <span key={b.name} className="inline-flex items-center gap-1.5 rounded-full bg-red-50 py-1 pl-3 pr-1 text-xs font-semibold text-red-900">
            {b.name}
            <Input
              className="h-7 w-16 border-0 bg-white px-1.5 text-center text-xs"
              value={String(b.amount).replace(".", ",")}
              data-testid={`base-prezzo-${idx}`}
              onChange={(e) => setBasi(basi.map((x, i) => (i === idx ? { ...x, amount: num(e.target.value) } : x)))}
            />
            <button className="rounded-full p-1 text-rose-600" data-testid={`base-togli-${idx}`}
                    onClick={() => setBasi(basi.filter((_, i) => i !== idx))}>
              <Trash2 className="h-3 w-3" />
            </button>
          </span>
        ))}
        {basi.length === 0 && <p className="text-sm text-slate-500">Nessuna base: aggiungine una qui sotto.</p>}
      </div>

      <Button size="sm" variant="outline" disabled={busy} data-testid="basi-consigliate"
              onClick={() =>
                salva([{ name: "Pomodoro", amount: 4.5 },
                       { name: "Bianca", amount: 5 },
                       { name: "Pomodoro e mozzarella", amount: 5 }],
                      [{ name: "Normale", amount: 0 }, { name: "Baby", amount: -0.5 },
                       { name: "Sottile", amount: 0 }])
              }>
        <Save className="mr-1.5 h-3.5 w-3.5" /> Metti basi 4,50 · 5,00 · 5,00 e baby −0,50
      </Button>

      <div className="flex flex-wrap items-center gap-2">
        <Input className="h-10 w-44" placeholder="nuova base (es. Bianca)" value={nome}
               onChange={(e) => setNome(e.target.value)} data-testid="base-nome" />
        <Input className="h-10 w-20 text-center" placeholder="5,00" value={prezzo}
               onChange={(e) => setPrezzo(e.target.value)} data-testid="base-importo" />
        <Button size="sm" variant="outline" data-testid="base-add"
                onClick={() => {
                  const n = nome.trim();
                  if (!n) return;
                  if (basi.some((b) => b.name.toLowerCase() === n.toLowerCase())) return toast.error("Base già presente");
                  setBasi([...basi, { name: n, amount: num(prezzo) }]);
                  setNome("");
                }}>
          <Plus className="mr-1 h-3.5 w-3.5" /> Aggiungi base
        </Button>
      </div>

      <div className="flex flex-wrap items-center gap-2 border-t border-slate-100 pt-3">
        <span className="text-xs font-semibold text-slate-600">Formati</span>
        {formati.map((f, idx) => (
          <span key={f.name} className="inline-flex items-center gap-1.5 rounded-full bg-slate-100 py-1 pl-3 pr-1 text-xs font-semibold text-slate-700">
            {f.name}
            <Input
              className="h-7 w-16 border-0 bg-white px-1.5 text-center text-xs"
              value={String(f.amount).replace(".", ",")}
              data-testid={`formato-prezzo-${idx}`}
              onChange={(e) => setFormati(formati.map((x, i) => (i === idx ? { ...x, amount: num(e.target.value) } : x)))}
            />
            <button className="rounded-full p-1 text-rose-600" data-testid={`formato-togli-${idx}`}
                    onClick={() => setFormati(formati.filter((_, i) => i !== idx))}>
              <Trash2 className="h-3 w-3" />
            </button>
          </span>
        ))}
        <Input className="h-10 w-36" placeholder="nuovo formato" value={fNome}
               onChange={(e) => setFNome(e.target.value)} data-testid="formato-nome" />
        <Input className="h-10 w-20 text-center" placeholder="0,00" value={fPrezzo}
               onChange={(e) => setFPrezzo(e.target.value)} data-testid="formato-importo" />
        <Button size="sm" variant="outline" data-testid="formato-add"
                onClick={() => {
                  const n = fNome.trim();
                  if (!n) return;
                  if (formati.some((f) => f.name.toLowerCase() === n.toLowerCase())) return toast.error("Formato già presente");
                  setFormati([...formati, { name: n, amount: num(fPrezzo) }]);
                  setFNome("");
                }}>
          <Plus className="mr-1 h-3.5 w-3.5" /> Aggiungi formato
        </Button>
      </div>

      <div className="flex flex-wrap items-center gap-2 border-t border-slate-100 pt-3">
        <span className="text-xs font-semibold text-slate-600">Impasti</span>
        {impasti.map((d, idx) => (
          <span key={d.name} className="inline-flex items-center gap-1.5 rounded-full bg-slate-100 py-1 pl-3 pr-1 text-xs font-semibold text-slate-700">
            {d.name}
            <Input
              className="h-7 w-16 border-0 bg-white px-1.5 text-center text-xs"
              value={String(d.amount).replace(".", ",")}
              data-testid={`impasto-prezzo-${idx}`}
              onChange={(e) => setImpasti(impasti.map((x, n) => (n === idx ? { ...x, amount: num(e.target.value) } : x)))}
            />
            <button className="rounded-full p-1 text-rose-600" data-testid={`impasto-togli-${idx}`}
                    onClick={() => setImpasti(impasti.filter((_, n) => n !== idx))}>
              <Trash2 className="h-3 w-3" />
            </button>
          </span>
        ))}
        <Input className="h-10 w-36" placeholder="nuovo impasto" value={iNome}
               onChange={(e) => setINome(e.target.value)} data-testid="impasto-nome" />
        <Input className="h-10 w-20 text-center" placeholder="0,00" value={iPrezzo}
               onChange={(e) => setIPrezzo(e.target.value)} data-testid="impasto-importo" />
        <Button size="sm" variant="outline" data-testid="impasto-add"
                onClick={() => {
                  const n = iNome.trim();
                  if (!n) return;
                  if (impasti.some((d) => d.name.toLowerCase() === n.toLowerCase())) return toast.error("Impasto già presente");
                  setImpasti([...impasti, { name: n, amount: num(iPrezzo) }]);
                  setINome("");
                }}>
          <Plus className="mr-1 h-3.5 w-3.5" /> Aggiungi impasto
        </Button>
      </div>

      <div className="flex flex-wrap items-center gap-2 border-t border-slate-100 pt-3">
        <span className="text-xs font-semibold text-slate-600">Ingrediente senza prezzo suo</span>
        <Input className="h-10 w-20 text-center" value={ingPrezzo}
               onChange={(e) => setIngPrezzo(e.target.value)} data-testid="ingrediente-prezzo" />
        <Button size="sm" disabled={busy} onClick={() => salva()} data-testid="pizza-inventata-salva">
          <Save className="mr-1 h-3.5 w-3.5" /> Salva
        </Button>
        {basi.length > 0 && (
          <span className="font-num text-[11px] text-slate-500">
            basi da {eur(Math.min(...basi.map((b) => b.amount)))} a {eur(Math.max(...basi.map((b) => b.amount)))}
          </span>
        )}
      </div>
    </Card>
  );
};
